import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';

// Types
import { AuthFormData } from '../../types/auth-form-data';

// Utils
import { copyQrCodeToNavigator } from '../../utils/user';

// Enums
import { AUTH_FLOW } from '../../enums/auth-step.enum';

// Store
import { RootState } from '../../store/store';

// Constants
import ROUTE from '../../constants/route-constants';

// Components
import EmailVerifyForm from '../../components//EmailVerifyForm/EmailVerifyForm';
import CodeForm from '../../components/CodeForm/CodeForm';
import IconMobileTailAdmin from '../../components/Icons/MobileTailAdmin/MobileTailAdmin';
import MfaForm from '../../components/MfaForm/MfaForm';
import PasswordForm from '../../components/PasswordForm/PasswordForm';
import MfaQrCode from '../../components/MfaQrCode/MfaQrCode';

// Types
type SignInProps = ReturnType<typeof mapState>;

const SignIn: React.FC<SignInProps> = ({ isAuthenticated, authFlow }) => {

  const isBasicSignIn = authFlow === AUTH_FLOW.BASIC;
  const isFirstSignIn = authFlow === AUTH_FLOW.FIRST_SIGN_IN;
  const isWithInvitationCode = authFlow === AUTH_FLOW.WITH_INVITATION_CODE;
  const isUnconfirmed = authFlow === AUTH_FLOW.UNCONFIRMED;
  const isMfaSetup = authFlow === AUTH_FLOW.MFA_SETUP;
  const isMfaCompleted = authFlow === AUTH_FLOW.MFA_SETUP_COMPLETED;
  const isMfaCheck = authFlow === AUTH_FLOW.MFA_CHECK;
  const isCheckEmail = authFlow === AUTH_FLOW.CHECK_EMAIL;

  const defaultFormData: AuthFormData = {
    email: null,
    password_digest: null,
  }

  const navigate = useNavigate();

  const [formData, setFormData] = useState<AuthFormData>(defaultFormData);

  // Logic
  if (isAuthenticated) {
    return <Navigate to={ROUTE.HOME} />;
  }

  if (isUnconfirmed) {
    console.error('isUnconfirmed');
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleCopyQrCode = async (text: string) => {
    const result = await copyQrCodeToNavigator(text);

    if (result) console.log('Copied to clipboard!');
    else console.error('Could not copy!');
  };

  const getTextFormByFlow = () => {
    switch (authFlow) {
    case AUTH_FLOW.MFA_SETUP:
      return (
        <>
          <h2 className="p-sign-in__title text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
              MFA Set Up
          </h2>
          <p className='p-sign-in__mfa-instruction font-satoshi pb-8 text-[16px] font-medium leading-[24px] text-left text-[#8A8A8A]'>Scan QR-code and enter in sequence the 6-digit codes from Authenticator.</p>
        </>
      );

    case AUTH_FLOW.MFA_SETUP_COMPLETED:
      return (
        <>
          <h2 className="p-sign-in__title text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
              MFA Set Up
          </h2>
          <p className='p-sign-in__mfa-instruction font-satoshi pb-8 text-[16px] font-medium leading-[24px] text-left text-[#8A8A8A]'>Scan QR-code and enter in sequence the 6-digit codes from Authenticator.</p>
        </>
      );

    case AUTH_FLOW.MFA_CHECK:
      return (
        <h2 className="p-sign-in__title text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
            MFA Check
        </h2>
      );
    default:
      return (
        <>
          <span className="p-sign-in__greetings pb-1.5 block font-medium">
              Welcome
          </span>
          <h2 className="p-sign-in__title text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
              Please Log In
          </h2>
        </>
      );
    }
  }

  return (
    <>
      <div className="p-sign-in h-screen flex md:items-center md:justify-center md:p-6">
        <div className={`p-sign-in__wrapper w-full md:w-auto grid rounded-sm items-center border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark ${(isMfaSetup || isMfaCompleted) ? 'grid-rows-[464px] lg:grid-cols-[minmax(auto,_564px)_minmax(350px,_564px)]' : 'md:grid-cols-[minmax(auto,_564px)_minmax(350px,_564px)] md:grid-rows-[600px]'}`}>
          <div className="p-sign-in__left-side flex justify-center items-center h-full w-full bg-primary">
            {(isMfaSetup || isMfaCompleted) ? (<MfaQrCode onClick={handleCopyQrCode} />) : (<IconMobileTailAdmin width="100%" height="100%" iconClassName='max-w-[389px] max-h-[350px] md:w-[389px] md:h-[350px]' />)}
          </div>
          <div className="p-sign-in__right-side flex flex-col w-full h-full py-13 px-11 border-stroke dark:border-strokedark xl:border-l-2">
            <div className='p-sign-in__text-form pb-9'>
              {getTextFormByFlow()}
            </div>
            <>
              {isCheckEmail && (
                <EmailVerifyForm onChange={handleChange} email={formData.email} />
              )}
              {(isBasicSignIn && formData.email) && (
                <PasswordForm onChange={handleChange} formData={formData} />
              )}
              {(isWithInvitationCode && formData.email) && (
                <CodeForm email={formData.email} />
              )}
              {(isFirstSignIn && formData.email) && (
                <PasswordForm isFirstSignIn onChange={handleChange} formData={formData} />
              )}
              {((isMfaCompleted || isMfaSetup || isMfaCheck) && formData.email) && (
                <MfaForm
                  formData={formData}
                  isMfaSetup={isMfaSetup}
                  isMfaCheck={isMfaCheck}
                  isMfaCompleted={isMfaCompleted}
                />
              )}
              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate(ROUTE.AUTH_FORGOT_PASSWORD)}
                  className="text-sm text-primary hover:text-primary-dark transition-colors"
                >
                  Forgot Password?
                </button>
              </div>
            </>
          </div>
        </div>
      </div>
    </>
  );
};

const mapState = (state: RootState) => ({
  authFlow: state.auth.authFlow,
  isAuthenticated: state.auth?.isAuthenticated,
});

export default connect(mapState, null)(SignIn);
